import React, { useState, useMemo } from "react";
import ConfirmPopUp from "./ConfirmPopUp";
import McqDisplayMcq from "./McqDisplayMcq";
import AttachDisplay from "./AttachDisplay";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Info } from "lucide-react";
import { useSelector } from "react-redux";

const ACCENT = "#F28C28";
const ACCENT_SOFT = "#FFF7ED";
const BORDER = "#E5E7EB";
const TEXT_PRIMARY = "#111827";
const TEXT_SECONDARY = "#6B7280";
const BG_SOFT = "#F9FAFB";

// Helper Component to render one Bar Row
const DistRow = ({ label, marks, count, maxMarks, color }) => {
  const percent = maxMarks > 0 ? (marks / maxMarks) * 100 : 0;
  return (
    <div className="d-flex align-items-center gap-3 mb-2">
      <Typography
        sx={{
          width: "130px",
          fontSize: "13px",
          fontWeight: 500,
          color: "#4B5563",
        }}
      >
        {label}
      </Typography>
      <div
        style={{
          flex: 1,
          height: "10px",
          backgroundColor: "#E5E7EB",
          borderRadius: "5px",
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: "100%",
            backgroundColor: color,
            borderRadius: "5px",
          }}
        ></div>
      </div>
      <Typography
        sx={{
          width: "100px",
          fontSize: "13px",
          fontWeight: 600,
          color: "#4B5563",
          textAlign: "right",
        }}
      >
        {marks} marks ({count}Q)
      </Typography>
    </div>
  );
};

export default function PublishPreviewQB({
  setPreview,
  displayQuestions,
  details,
  type,
  selectedBox,
}) {
  const [publishQb, setPubishQb] = useState(false);

  // Redux states to fetch labels
  const difficultyData = useSelector(
    (state) => state.initialData.difficultyLevels,
  );
  const skillsData = useSelector((state) => state.initialData.skills);
  const questionTypesData = useSelector(
    (state) => state.initialData.questionTypes,
  );

  const totalMarks = (displayQuestions || []).reduce(
    (acc, q) => acc + (Number(q?.correct_score) || 0),
    0,
  );

  // Calculate Incomplete Questions
  const incompleteCount = (displayQuestions || []).filter((q) => {
    const isMissingBasic =
      !q.title ||
      !q.skill_id ||
      !q.type_id ||
      !q.chapter_id ||
      !q.correct_score ||
      !q.difficulty_level;
    let isMissingOptions = false;
    if (q.type_id) {
      if (Array.isArray(q.options) && q.options.length > 0)
        isMissingOptions = q.options.some((opt) => !opt?.key || !opt?.value);
      if (Array.isArray(q.correct_answers))
        isMissingOptions =
          isMissingOptions ||
          q.correct_answers.some(
            (ans) =>
              q?.options?.length > 0 &&
              (!ans?.option_key || ans.option_key.trim() === ""),
          );
    }
    return isMissingBasic || isMissingOptions;
  }).length;

  // 👈 CALCULATE REAL DATA FOR CHARTS
  const { difficultyStats, skillStats, typeStats, chapterStats } =
    useMemo(() => {
      const diff = {};
      const skill = {};
      const type = {};
      const chap = {};

      (displayQuestions || []).forEach((q) => {
        const marks = Number(q.correct_score) || 0;

        // 1. Difficulty
        const diffId = q.difficulty_level || "unknown";
        if (!diff[diffId]) diff[diffId] = { marks: 0, count: 0 };
        diff[diffId].marks += marks;
        diff[diffId].count += 1;

        // 2. Skill
        const skillId = q.skill_id || "unknown";
        if (!skill[skillId]) skill[skillId] = { marks: 0, count: 0 };
        skill[skillId].marks += marks;
        skill[skillId].count += 1;

        // 3. Question Type
        const typeId = q.type_id || "unknown";
        if (!type[typeId]) type[typeId] = { marks: 0, count: 0 };
        type[typeId].marks += marks;
        type[typeId].count += 1;

        // 4. Chapter
        const chapId = q.chapter_id || "unknown";
        if (!chap[chapId]) chap[chapId] = { marks: 0, count: 0 };
        chap[chapId].marks += marks;
        chap[chapId].count += 1;
      });

      return {
        difficultyStats: diff,
        skillStats: skill,
        typeStats: type,
        chapterStats: chap,
      };
    }, [displayQuestions]);

  return (
    <>
      {!publishQb && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            p: 2,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 0,
              borderRadius: "16px",
              background: BG_SOFT,
              border: `1px solid ${BORDER}`,
              position: "relative",
              width: { xs: "96%", sm: "92%", md: "88%", lg: "1200px" },
              maxWidth: "1200px",
              maxHeight: "95vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <IconButton
              onClick={() => setPreview(false)}
              size="small"
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: TEXT_SECONDARY,
                background: "#fff",
                border: `1px solid ${BORDER}`,
                zIndex: 10,
                "&:hover": { background: ACCENT_SOFT, color: ACCENT },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Header Bar */}
            <Box sx={{ p: "24px 32px 16px", background: BG_SOFT }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: TEXT_PRIMARY,
                  mb: 1,
                  letterSpacing: "-0.5px",
                }}
              >
                Review & Save Question Bank
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: TEXT_SECONDARY }}>
                  <b style={{ color: TEXT_PRIMARY }}>
                    {details?.name || "Question Bank"}
                  </b>{" "}
                  · Review questions, distribution and validation before saving.
                </Typography>
                <Chip
                  icon={
                    <CheckCircleIcon
                      fontSize="small"
                      style={{ color: "#065F46" }}
                    />
                  }
                  label="Ready to save"
                  size="small"
                  sx={{
                    backgroundColor: "#D1FAE5",
                    color: "#065F46",
                    fontWeight: 600,
                    fontSize: "12px",
                    px: 1,
                  }}
                />
              </Box>
            </Box>

            {/* Scrollable Content */}
            <Box
              sx={{
                overflowY: "auto",
                px: "32px",
                pb: "32px",
                flex: 1,
                minHeight: 0,
                background: BG_SOFT,
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  background: "#CBD5E1",
                  borderRadius: "10px",
                },
              }}
            >
              {/* Stats Cards */}
              <div className="row g-3 mb-4 mt-1">
                <div className="col-md-4">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: TEXT_PRIMARY,
                        lineHeight: 1.2,
                      }}
                    >
                      {displayQuestions?.length || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: TEXT_SECONDARY,
                      }}
                    >
                      Total questions
                    </Typography>
                  </Paper>
                </div>
                <div className="col-md-4">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: TEXT_PRIMARY,
                        lineHeight: 1.2,
                      }}
                    >
                      {totalMarks}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: TEXT_SECONDARY,
                      }}
                    >
                      Total marks
                    </Typography>
                  </Paper>
                </div>
                <div className="col-md-4">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "28px",
                        fontWeight: 700,
                        color: TEXT_PRIMARY,
                        lineHeight: 1.2,
                      }}
                    >
                      {incompleteCount}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 500,
                        color: TEXT_SECONDARY,
                      }}
                    >
                      Incomplete questions
                    </Typography>
                  </Paper>
                </div>
              </div>

              {/* Info Banner */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  borderRadius: "8px",
                  padding: "12px 16px",
                  fontSize: "13px",
                  color: "#1E40AF",
                  mb: 3,
                }}
              >
                <Info size={16} />
                <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                  All questions pass the prototype's required-field validation.
                  Review content accuracy before finalizing.
                </Typography>
              </Box>

              {/* Distribution Charts */}
              <div className="row g-4 mb-4">
                {/* 1. Difficulty */}
                <div className="col-md-6">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        mb: 2,
                      }}
                    >
                      Difficulty{" "}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                          color: TEXT_SECONDARY,
                        }}
                      >
                        (marks allocated)
                      </span>
                    </Typography>
                    <div className="d-flex flex-column gap-2">
                      {difficultyData?.map((level, index) => {
                        const stats = difficultyStats[level.id] || {
                          marks: 0,
                          count: 0,
                        };
                        return (
                          <DistRow
                            key={index}
                            label={level.name}
                            marks={stats.marks}
                            count={stats.count}
                            maxMarks={totalMarks}
                            color="#10B981"
                          />
                        );
                      })}
                      {difficultyStats["unknown"] && (
                        <DistRow
                          label="Unclassified"
                          marks={difficultyStats["unknown"].marks}
                          count={difficultyStats["unknown"].count}
                          maxMarks={totalMarks}
                          color="#9CA3AF"
                        />
                      )}
                    </div>
                  </Paper>
                </div>

                {/* 2. Bloom's Skills */}
                <div className="col-md-6">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        mb: 2,
                      }}
                    >
                      Bloom's Skills{" "}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                          color: TEXT_SECONDARY,
                        }}
                      >
                        (marks allocated)
                      </span>
                    </Typography>
                    <div className="d-flex flex-column gap-2">
                      {skillsData?.map((skill, index) => {
                        const stats = skillStats[skill.id] || {
                          marks: 0,
                          count: 0,
                        };
                        return (
                          <DistRow
                            key={index}
                            label={skill.name}
                            marks={stats.marks}
                            count={stats.count}
                            maxMarks={totalMarks}
                            color="#6366F1"
                          />
                        );
                      })}
                      {skillStats["unknown"] && (
                        <DistRow
                          label="Unclassified"
                          marks={skillStats["unknown"].marks}
                          count={skillStats["unknown"].count}
                          maxMarks={totalMarks}
                          color="#9CA3AF"
                        />
                      )}
                    </div>
                  </Paper>
                </div>

                {/* 3. Question Types */}
                <div className="col-md-6">
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      border: `1px solid ${BORDER}`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "16px",
                        fontWeight: 600,
                        color: TEXT_PRIMARY,
                        mb: 2,
                      }}
                    >
                      Question Types{" "}
                      <span
                        style={{
                          fontSize: "13px",
                          fontWeight: 400,
                          color: TEXT_SECONDARY,
                        }}
                      >
                        (marks allocated)
                      </span>
                    </Typography>
                    <div className="d-flex flex-column gap-2">
                      {questionTypesData?.map((type, index) => {
                        const stats = typeStats[type.id] || {
                          marks: 0,
                          count: 0,
                        };
                        return (
                          <DistRow
                            key={index}
                            label={type.name}
                            marks={stats.marks}
                            count={stats.count}
                            maxMarks={totalMarks}
                            color="#F59E0B"
                          />
                        );
                      })}
                      {typeStats["unknown"] && (
                        <DistRow
                          label="Unclassified"
                          marks={typeStats["unknown"].marks}
                          count={typeStats["unknown"].count}
                          maxMarks={totalMarks}
                          color="#9CA3AF"
                        />
                      )}
                    </div>
                  </Paper>
                </div>
              </div>

              {/* Question List */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  border: `1px solid ${BORDER}`,
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "18px",
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                    mb: 2,
                  }}
                >
                  Question-by-question review
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <div className="d-flex flex-column gap-3">
                  {displayQuestions?.length > 0 ? (
                    displayQuestions.map((question, index) => {
                      const isMcq = question?.question_type === "false";
                      return isMcq ? (
                        <McqDisplayMcq
                          key={index}
                          nextQuestion={index}
                          question={question}
                        />
                      ) : (
                        <AttachDisplay
                          key={index}
                          nextQuestion={index}
                          question={question}
                          selectedBox={selectedBox}
                        />
                      );
                    })
                  ) : (
                    <Typography
                      align="center"
                      color="text.secondary"
                      sx={{ py: 4 }}
                    >
                      No questions to review.
                    </Typography>
                  )}
                </div>
              </Paper>
            </Box>

            {/* Footer Bar */}
            <Box
              sx={{
                p: "16px 32px",
                borderTop: `1px solid ${BORDER}`,
                background: "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: TEXT_SECONDARY,
                }}
              >
                Auto-saved locally
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setPreview(false)}
                  sx={{
                    borderColor: "#D1D5DB",
                    color: TEXT_PRIMARY,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    "&:hover": { borderColor: "#9CA3AF", background: BG_SOFT },
                  }}
                >
                  Edit Questions
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => setPubishQb(true)}
                  sx={{
                    background: ACCENT,
                    color: "#fff",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "14px",
                    px: 3,
                    boxShadow: "0 4px 12px rgba(242,140,40,0.25)",
                    "&:hover": { background: "#e07c24" },
                  }}
                >
                  Save to Question Bank
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Confirm Popup */}
      {publishQb && type === "create" && (
        <ConfirmPopUp
          close={setPubishQb}
          questions={displayQuestions}
          basicDetails={details}
          type={"create"}
        />
      )}
      {publishQb && type !== "create" && (
        <ConfirmPopUp
          close={setPubishQb}
          questions={displayQuestions}
          basicDetails={details}
        />
      )}
    </>
  );
}
